import { IconLoader } from "@tabler/icons-react";
import { type FocusEventHandler, type ReactNode } from "react";
import Select, {
  type GroupBase,
  type InputActionMeta,
  type OptionsOrGroups,
  components as defaultComponents,
} from "react-select";

interface BaseSelectOption {
  label: ReactNode;
  value: string | boolean;
}

interface InputSelectProps<T extends BaseSelectOption> {
  errorMessage?: string | boolean;
  infoMessage?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isOptional?: boolean;
  label?: string;
  maxWidth?: string;
  name?: string;
  onBlur?: FocusEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
  onChange?: (newValue: T | T[] | null) => void;
  placeholder?: string;
  successMessage?: string;
  value?: T | T[] | null;
  options: OptionsOrGroups<T, GroupBase<T>>;
  formatOption?: (option: T) => ReactNode;
  formatSelectedValue?: (option: T) => ReactNode;
  components?: Partial<typeof defaultComponents>;
  iconLabel?: ReactNode;
  small?: boolean;
  isMulti?: boolean;
  required?: boolean;
  isClearable?: boolean;
  /** Con búsqueda en servidor, usar `() => true` para no filtrar de nuevo en el cliente. */
  filterOption?: (option: { data: T }, inputValue: string) => boolean;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  controlClassName?: string; // Clases personalizadas para el control del select
  /** Mensaje cuando no hay opciones (react-select). */
  noOptionsMessage?: (props: { inputValue: string }) => string;
}

export const InputSelect = <T extends BaseSelectOption>({
  errorMessage,
  infoMessage,
  isDisabled = false,
  isLoading = false,
  label,
  maxWidth = "max-w-xl",
  name,
  onBlur,
  onFocus,
  onChange,
  placeholder = "Seleccionar...",
  successMessage,
  value,
  options,
  isOptional = false,
  formatOption,
  formatSelectedValue,
  components,
  iconLabel,
  small,
  isMulti,
  required,
  isClearable = false,
  filterOption,
  onInputChange,
  controlClassName = "",
  noOptionsMessage,
}: InputSelectProps<T>) => {
  const renderMessage = () => {
    if (errorMessage) {
      return (
        <span className="text-red-500 dark:text-red-400 font-medium text-sm" role="alert">
          {errorMessage}
        </span>
      );
    }
    if (successMessage) {
      return (
        <span className="text-green-500 dark:text-green-400 font-medium text-sm" role="status">
          {successMessage}
        </span>
      );
    }
    if (infoMessage) {
      return <span className="text-zinc-500 dark:text-zinc-300 font-medium text-sm">{infoMessage}</span>;
    }
    return null;
  };

  return (
    <div className={`w-full ${maxWidth} flex flex-col gap-2`}>
      {label && (
        <label
          htmlFor={name}
          className="dark:text-white/80 text-zinc-600 text-base font-medium flex items-center"
        >
          {label}
          {isOptional && <span className="text-black/40 dark:text-white/60 ml-1">(opcional)</span>}
          {iconLabel && iconLabel}
        </label>
      )}

      <div className="relative w-full">
        <Select<T, boolean>
          inputId={name}
          required={required}
          name={name}
          value={value}
          onChange={(newValue) => {
            if (onChange) {
              if (Array.isArray(newValue)) {
                onChange([...newValue] as T[]);
              } else {
                onChange(newValue as T | null);
              }
            }
          }}
          onBlur={onBlur}
          onFocus={onFocus}
          options={options}
          isDisabled={isDisabled}
          isLoading={isLoading}
          isMulti={isMulti}
          isClearable={isClearable}
          {...(filterOption ? { filterOption } : {})}
          {...(noOptionsMessage ? { noOptionsMessage } : {})}
          onInputChange={onInputChange}
          placeholder={placeholder}
          formatOptionLabel={(option, { context }) =>
            context === "menu"
              ? formatOption
                ? formatOption(option)
                : option.label
              : formatSelectedValue
                ? formatSelectedValue(option)
                : option.label
          }
          components={components}
          aria-invalid={!!errorMessage}
          classNames={{
            // `small` used to render as `!h-8` glued onto `!h-12` with no space — the single token
            // `!h-8!h-12`, which is not a class — so small selects fell back to react-select's own
            // `min-height: 38px` (set through emotion). A height alone cannot go under that min-height,
            // hence the pair. `!h-9` (36px) matches the design system's default button height, which
            // these page-size selects sit beside. The leading `!` is still valid in Tailwind v4:
            // `!h-12` does apply, so every select without `small` is 48px tall.
            control: ({ isFocused }) =>
              `${small ? "!h-9 !min-h-9" : "!h-12"} !bg-white dark:!bg-white/10 dark:!text-white !shadow-none ${
                isFocused
                  ? "!border-green-500 dark:!border-green-300"
                  : "!border-black/20 dark:!border-white/30"
              }${controlClassName ? ` ${controlClassName}` : ""}`,
            option: ({ isSelected }) =>
              `${
                isSelected
                  ? "!bg-green-50 !font-medium !text-green-600 dark:!bg-white/10 dark:!text-green-300"
                  : "!bg-white dark:!bg-zinc-800 !text-zinc-700 dark:!text-zinc-100"
              }`,
            singleValue: () => "dark:!text-white",
            indicatorSeparator: () => "!bg-zinc-200 dark:!bg-zinc-600",
            menu: () => "dark:!bg-zinc-800 dark:!border-zinc-500 dark:!border",
            placeholder: () => "!text-zinc-400 truncate",
            input: () => "dark:!text-white truncate",
            multiValueLabel: () =>
              "bg-green-100 dark:bg-green-800 !text-green-500 font-medium dark:!text-green-300",
            multiValueRemove: () => "bg-green-100 dark:bg-green-800 ",
            multiValue: () => "bg-green-100 dark:bg-green-800 ",
          }}
          menuPlacement="auto"
        />

        {isLoading && (
          <div className="absolute inset-y-0 right-3 flex items-center text-zinc-400 pointer-events-none">
            <IconLoader className="animate-spin animate-duration-[2s]" />
          </div>
        )}
      </div>

      {renderMessage() && <div className="-mt-1 flex justify-start flex-col">{renderMessage()}</div>}
    </div>
  );
};
